/* ============================================================
   ClientCoreProvider — the root the rest of this package hangs off. A front
   end mounts it ONCE, above AuthProvider, with its config (where the API and
   the user pool are) and its adapters (how this platform stores, opens a
   browser, notices the background). Everything below reads both from here.

   The bound helpers (persist, the token store, the Cognito calls) are built
   here too, so there is exactly one of each per app and no module-level
   singleton that a second platform would have to reconfigure.
   ============================================================ */
import React, { createContext, useContext, useMemo } from 'react';

import type { ClientCoreAdapters } from './adapters';
import { type CognitoApi, createCognitoApi } from './auth/cognitoApi';
import type { CognitoConfig } from './auth/config';
import { createTokenStore, type TokenStore } from './auth/tokens';
import { createPersist, type Persist } from './store/persist';

export interface ClientCoreConfig {
  /** A bare origin when infra sets a custom API domain; carries the stage
     segment when it falls back to the execute-api host. Paths are appended
     verbatim either way. */
  apiUrl: string;
  cognito: CognitoConfig;
  /** Who is calling, so the server can refuse a build below its floor with a
     426 (backend/src/client-version.ts): `X-App-Platform` / `X-App-Build`.
     Empty where there is no native build — the server is lenient on anything
     it can't read. */
  clientHeaders: Record<string, string>;
}

export interface ClientCoreValue {
  config: ClientCoreConfig;
  adapters: ClientCoreAdapters;
  persist: Persist;
  tokens: TokenStore;
  cognito: CognitoApi;
}

const Ctx = createContext<ClientCoreValue | null>(null);

export function ClientCoreProvider({
  config,
  adapters,
  children,
}: {
  config: ClientCoreConfig;
  adapters: ClientCoreAdapters;
  children: React.ReactNode;
}) {
  const value = useMemo<ClientCoreValue>(
    () => ({
      config,
      adapters,
      persist: createPersist(adapters.storage),
      tokens: createTokenStore(adapters.tokenStorage),
      cognito: createCognitoApi(config.cognito),
    }),
    [config, adapters],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useClientCore(): ClientCoreValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useClientCore must be used within ClientCoreProvider');
  return v;
}

/** The disk layer, bound to this platform's storage. */
export function usePersist(): Persist {
  return useClientCore().persist;
}
